import { Box, Button, Container } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import ChangeReasonDialog from "components/ChangeReasonDialog";
import EntityDetails from "components/EntityDetails";
import RedirectBackButton from "components/RedirectBackButton";
import RowElement from "components/RowElement";
import SnackbarMessage from "components/SnackbarMessage";
import { makeStyles } from "helpers/StyleHelper";
import getUserDisplayName from "helpers/userHelper";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import {
  MarkAsAttendedRequest,
  OrganizationUserDetails,
  ResponseOfUserSkuExtended,
} from "services/KnowfullyAdminClient";
import {
  Severity,
  SnackbarMessageProps,
  UserSkuStates,
} from "types/common-types";
import { convertToZonedDate } from "utils/dateUtils";

const useStyles = makeStyles((theme: Theme) => ({
  unlockPostEval: {
    margin: "30px 0",
  },
  confirmationMessageBlock: {
    margin: "15px 0",
  },
}));

const MarkUserAsAttendedPage = () => {
  const { userId, userSkuId } = useParams<{
    userId: string;
    userSkuId: string;
  }>();
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const { registerPromise } = useTrashablePromises();
  const queryClient = useQueryClient();

  const {
    organizationUserService,
    userSkuService,
    userDetailsUrl,
  } = useGlobalContext();

  const [markingAsAttended, setMarkingAsAttended] = useState(false);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [confirmDialogOpen, confirmDialogOpenActions] = useBoolean(false);
  const [changeReason, setChangeReason] = useState("");

  const userResult = useStrictQuery<OrganizationUserDetails>(
    ["getOrganizationUserDetails", userId],
    () => organizationUserService.getOrganizationUserDetails(userId!),
    {
      enabled: !!userId,
    }
  );

  const user = userResult?.data;
  const userName = getUserDisplayName(user, t({ id: "user" }));
  useTitle(t({ id: "markUserAsAttendedTitle" }, { name: userName }));

  const userSkuResult = useStrictQuery<ResponseOfUserSkuExtended>(
    ["getUserSku", userSkuId],
    () =>
      userSkuService.getUserSku(
        user?.id,
        user?.organizationId,
        userSkuId!
      ),
    { keepPreviousData: true, enabled: !!userResult && !userResult.isFetching }
  );

  const userSku = userSkuResult?.data?.data!;
  const isUserSkuExpired = userSku?.userSkuStateName === UserSkuStates.Expired;

  const expiredUserSkuRows = isUserSkuExpired
    ? [
        RowElement(
          "statusLabel",
          userSku.userSkuStateName ?? "",
          userSkuResult.isLoading
        ),
        RowElement(
          "endDateLabel",
          userSku.endDate ? convertToZonedDate(userSku.endDate)! : "",
          userSkuResult.isLoading
        ),
      ]
    : [];

  const rows = [
    RowElement("userNameLabel", userName!, userResult.isLoading),
    RowElement("courseName", userSku?.title ?? "", userSkuResult.isLoading),
    RowElement("skuLabel", userSku?.sku ?? "", userSkuResult.isLoading),
    ...expiredUserSkuRows,
  ].filter((r) => !!r.component);

  const markAsAttended = async () => {
    setMarkingAsAttended(true);

    const markAsAttendedRequest = MarkAsAttendedRequest.fromJS({
      organizationId: user?.organizationId,
      userId: user?.id,
      userSkuId,
      changeReason,
    });

    try {
      await registerPromise(
        userSkuService.markAsAttended(markAsAttendedRequest)
      );
      queryClient.invalidateQueries(["getUserSku", userSkuId]);
      confirmDialogOpenActions.setFalse();
      setSnackbarMessage(
        new SnackbarMessageProps(
          t({ id: "markAsAttendedSuccessMessage" }),
          Severity.Success
        )
      );
    } finally {
      setMarkingAsAttended(false);
    }
  };

  return (
    <Container>
      <RedirectBackButton
        redirectRoute={userDetailsUrl(userId)}
        labelKey="backToUser"
      />

      <h1>{t({ id: "markAsAttendedMessage" })}</h1>

      <EntityDetails details={rows} />

      <Box sx={styles.unlockPostEval}>
        <Button
          variant="outlined"
          disabled={markingAsAttended || !user || !userSku}
          onClick={confirmDialogOpenActions.setTrue}
        >
          <T id="markAsAttendedMessage" />
        </Button>
      </Box>

      <ChangeReasonDialog
        open={confirmDialogOpen}
        onCancel={confirmDialogOpenActions.setFalse}
        handleConfirm={markAsAttended}
        changeReason={changeReason}
        setChangeReason={setChangeReason}
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default MarkUserAsAttendedPage;
